import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import type { ComponentType, FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { retrieveToken, storeToken } from '../auth-token';
import Error500 from '../components/error500/error500';
import { safeChangeLanguage, useAppTranslation } from '../i18n';
import { useAppDispatch, useAppSelector } from '../store';
import { ROUTE_TYPE } from '../store/actions/action-types';
import { loadInstanceInfo } from '../store/actions/instance-info-action';
import { loadSurveyTheme } from '../store/actions/survey-theme-actions';
import { loadUserBalance } from '../store/actions/user-balance-actions';
import { refreshProfile } from '../store/actions/user-profile-actions';
import { fetchDomainInfo } from '../store/reducers/domain-info-reducer';
import { setSelectedPanel } from '../store/reducers/panel-info-reducer';
import type { RouteTypeReducerState } from '../store/reducers/route-type-reducer';
import GenericLoader from '../survey-tool/components/generic-loader';
import { useAppNavigate } from '../utils/browser-navigate';
import { logErrorWithContext } from '../utils/log';
import { preserveWindowQuery } from '../utils/uri';

// Derived from `logErrorWithContext` rather than hand-copied, so the two cannot drift.
type RouteDiagnostics = Parameters<typeof logErrorWithContext>[1];

// Every caller passes a literal; `null` only exists in `RouteTypeReducerState` because the redux
// slice starts out unset.
type RouteType = Exclude<RouteTypeReducerState, null>;

export interface RouteDiagnosticsInput {
  routeType: RouteType;
  ppToken: string | null;
  loadingPrivate: boolean;
  loadingPublic: boolean;
  isPrivate: boolean;
  isPublic: boolean;
  domainInfo: { panels?: unknown[] } | null;
  userProfile: unknown;
  userBalance: unknown;
  instanceInfo: unknown;
  panelInfo: unknown;
  surveyThemeLoaded: unknown;
}

// Exported for its own unit test: the `{}`-vs-`null` distinction below is the entire point of the
// payload, and it is not reachable through the hook (the branch that consumes it is unreachable —
// see the guards), so a table test on the pure builder is the only way to protect it.
//
// `hasDomainInfo` is bare presence, NOT `!isEmpty(...)`: auth-api answers `{}` for a host it
// doesn't know, and folding that to `false` would make it indistinguishable from "no response
// yet" — both would also report `domainPanelCount: 0`. Presence here, emptiness via the count.
export function buildRouteDiagnostics(input: RouteDiagnosticsInput): RouteDiagnostics {
  return {
    routeType: input.routeType,
    hasToken: !!input.ppToken,
    loadingPrivate: input.loadingPrivate,
    loadingPublic: input.loadingPublic,
    isPrivate: input.isPrivate,
    isPublic: input.isPublic,
    hasDomainInfo: !!input.domainInfo,
    domainPanelCount: input.domainInfo?.panels?.length ?? 0,
    hasUserProfile: !!input.userProfile,
    hasUserBalance: !!input.userBalance,
    hasInstanceInfo: !!input.instanceInfo,
    hasPanelInfo: !!input.panelInfo,
    surveyThemeLoaded: !!input.surveyThemeLoaded,
  };
}

// `setE` only ever holds an `Error`, so `error` below can derive a message from anything a
// rejected thunk carries — a bare string or an axios error without `message` would otherwise
// derive to `null` and leave the user on the loader with nothing reported.
const toError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

const usePrivateOrPublic = (routeType: RouteType) => {
  const { i18n } = useAppTranslation();
  const dispatch = useAppDispatch();
  const currentRouteType = useAppSelector((s) => s.routeType);
  if (currentRouteType !== routeType) dispatch({ type: ROUTE_TYPE, routeType });

  // any vars
  const domainInfo = useAppSelector((state) => state.domainInfo);

  // private vars
  const [ppToken] = useState<string | null>(retrieveToken);
  const userProfile = useAppSelector((state) => state.userProfile);
  const userBalance = useAppSelector((state) => state.userBalance);
  const instanceInfo = useAppSelector((state) => state.instanceInfo);
  const panelInfo = useAppSelector((state) => state.panelInfo);
  const surveyThemeLoaded = useAppSelector((state) => state.surveyThemeLoaded);

  // public vars
  const [loadingPrivate, setLoadingPrivate] = useState<boolean>(true);
  const [loadingPublic, setLoadingPublic] = useState<boolean>(true);
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [e, setE] = useState<Error | null>(null);
  // Derived, not stored: a stored copy published the failure one render late, so a render that
  // switched `loading` off in the same batch was seen by the guards as a missing verdict and
  // redirected to /error, discarding the real message. PUR-4959.
  // Consumers must test `=== null`, not truthiness — `new Error()` has an empty message.
  const error = e === null ? null : e.message;

  useEffect(() => {
    dispatch(fetchDomainInfo()).catch((err) => setE(toError(err)));
  }, [dispatch]);

  useEffect(() => {
    if (domainInfo && !isEmpty(domainInfo)) {
      const currentLanguage = i18n.language;

      const allLanguages = flatten(domainInfo?.panels?.map((p) => p.languages)).filter((l) => l != null);
      if (allLanguages.length === 0) {
        safeChangeLanguage('en');
        return;
      }

      const hasCurrentLanguage = allLanguages.find((l) => l.language_code === currentLanguage);
      if (!hasCurrentLanguage) {
        const hasDefault = allLanguages.find((l) => l.is_default);
        const hasEnglish = allLanguages.find((l) => l.language_code === 'en' || l.language_code.startsWith('en-'));
        safeChangeLanguage(hasDefault?.language_code ?? hasEnglish?.language_code ?? allLanguages[0].language_code);
      }
    }
  }, [i18n, domainInfo]);

  useEffect(() => {
    if (ppToken) {
      setLoadingPublic(false);
      if (!userProfile || userProfile.ppToken !== ppToken) {
        if (ppToken) {
          storeToken(ppToken);
        }
        dispatch(refreshProfile()).catch((err) => setE(toError(err)));
      }
    } else {
      // we don't have ppToken
      setLoadingPrivate(false);
    }
  }, [dispatch, ppToken, userProfile]);

  useEffect(() => {
    if (userProfile) {
      // userProfile was loaded
      if (!userBalance) {
        dispatch(loadUserBalance()).catch((err) => setE(toError(err)));
      } else if (
        (userProfile.instanceKey && (!instanceInfo || instanceInfo.key !== userProfile.instanceKey)) ||
        (userProfile.panelKey && (!panelInfo || panelInfo.guid !== userProfile.panelKey))
      ) {
        // this will also load panelInfo
        dispatch(loadInstanceInfo(userProfile)).catch((err) => setE(toError(err)));
      } else if (!surveyThemeLoaded) {
        dispatch(loadSurveyTheme(panelInfo)).catch((err) => setE(toError(err)));
      }
    }
  }, [dispatch, userProfile, userBalance, instanceInfo, panelInfo, surveyThemeLoaded]);

  useEffect(() => {
    if (ppToken && userProfile && userBalance && instanceInfo && panelInfo) {
      setLoadingPrivate(false);
      setIsPrivate(true);
    }
  }, [ppToken, userProfile, userBalance, instanceInfo, panelInfo]);

  useEffect(() => {
    if (domainInfo && !loadingPrivate) {
      // domainInfo was loaded
      if (!panelInfo) {
        const firstPanel = domainInfo.panels?.[0];
        if (firstPanel) {
          dispatch(setSelectedPanel(firstPanel.uuid)).catch((err) => setE(toError(err)));
        } else {
          setLoadingPublic(false);
          setE(new Error(`Unrecognized domain: ${window.location.hostname}`));
        }
      }
    }
  }, [dispatch, domainInfo, loadingPrivate, panelInfo]);

  useEffect(() => {
    if (domainInfo && !loadingPrivate && instanceInfo && panelInfo) {
      setLoadingPublic(false);
      setIsPublic(true);
    }
  }, [domainInfo, loadingPrivate, instanceInfo, panelInfo]);

  // Presence flags and counts only — no identifiers, no tokens. Memoised because it feeds the
  // guards' effect deps.
  const diagnostics = useMemo<RouteDiagnostics>(
    () =>
      buildRouteDiagnostics({
        routeType,
        ppToken,
        loadingPrivate,
        loadingPublic,
        isPrivate,
        isPublic,
        domainInfo,
        userProfile,
        userBalance,
        instanceInfo,
        panelInfo,
        surveyThemeLoaded,
      }),
    [
      routeType,
      ppToken,
      loadingPrivate,
      loadingPublic,
      isPrivate,
      isPublic,
      domainInfo,
      userProfile,
      userBalance,
      instanceInfo,
      panelInfo,
      surveyThemeLoaded,
    ]
  );

  return {
    loading: loadingPrivate || loadingPublic,
    isPrivate,
    isPublic: isPrivate ? false : isPublic,
    error,
    diagnostics,
  };
};

export interface RouteProps {
  component: ComponentType;
}

export const PrivateRoute: FC<RouteProps> = ({ component }) => {
  const navigate = useAppNavigate();
  const { loading, isPrivate, isPublic, error, diagnostics } = usePrivateOrPublic('private');
  // One predicate, consumed by both the effect and the render body, so the two cannot drift.
  // Neither "still loading" nor "failed" is a verdict about the route, hence the `error` term.
  const willRedirect = !loading && error === null && !isPrivate;
  const redirected = useRef(false);

  // Redirecting is a side effect; from the render body it re-fired on every render. The ref makes
  // it one-shot, so later store churn cannot trigger a second navigation or log event.
  useEffect(() => {
    if (!willRedirect || redirected.current) {
      return;
    }
    redirected.current = true;
    if (isPublic) {
      navigate(
        preserveWindowQuery('/login', { redirect: `https://${window.location.host}${window.location.pathname}` })
      );
      return;
    }
    // Unreachable while the only branch that clears a loading flag alongside `setE` also supplies
    // a real Error (the unrecognized-domain branch). If a future edit breaks that, this is the
    // only artifact left, so it ships every input the guards key off.
    logErrorWithContext('Invalid state in PrivateRoute', diagnostics);
    navigate(preserveWindowQuery('/error'));
  }, [willRedirect, isPublic, navigate, diagnostics]);

  if (error !== null) {
    return <Error500 title={error} />;
  }
  if (loading) {
    return <GenericLoader />;
  }
  if (willRedirect) {
    return null;
  }
  const Component = component as any;
  return <Component />;
};

export const PublicRoute: FC<RouteProps> = ({ component }) => {
  const navigate = useAppNavigate();
  const { loading, isPrivate, isPublic, error, diagnostics } = usePrivateOrPublic('public');
  // Mirrors PrivateRoute above — see the comments there.
  const willRedirect = !loading && error === null && !isPublic;
  const redirected = useRef(false);

  useEffect(() => {
    if (!willRedirect || redirected.current) {
      return;
    }
    redirected.current = true;
    if (isPrivate) {
      navigate(preserveWindowQuery('/feed'));
      return;
    }
    logErrorWithContext('Invalid state in PublicRoute', diagnostics);
    navigate(preserveWindowQuery('/error'));
  }, [willRedirect, isPrivate, navigate, diagnostics]);

  if (error !== null) {
    return <Error500 title={error} />;
  }
  if (loading) {
    return <GenericLoader />;
  }
  if (willRedirect) {
    return null;
  }
  const Component = component as any;
  return <Component />;
};

export const AnyRoute: FC<RouteProps> = ({ component }) => {
  const { loading, error } = usePrivateOrPublic('any');
  if (error !== null) {
    return <Error500 title={error} />;
  }
  if (loading) {
    return <GenericLoader />;
  }
  const Component = component as any;
  return <Component />;
};
